import { useCallback, useEffect, useMemo, useState } from "react";
import type { ConfiguratorConfiguration } from "../contract/configuration";
import { usePriceReferenceSelection } from "~/app/config/useSession";
import { toSelectOption } from "~/domains/priceReference/priceReferenceSelection";

export interface CommerceSubmission {
  configId: string;
  quantity: number;
  priceReference: string;
}

export interface ConfiguratorCommerceRuntime {
  quantity: number;
  settledQuantity: number;
  priceReference: string;
  priceReferences: readonly { label: string; value: string }[];
  priceReferenceEditable: boolean;
  isSettling: boolean;
  submission: CommerceSubmission | null;
  setQuantity(quantity: number): void;
  setPriceReference(priceReference: string): void;
  markSubmitted(configId?: string): void;
  clearSubmission(): void;
}

const normalizeQuantity = (quantity: number) => (Number.isInteger(quantity) && quantity > 0 ? quantity : 1);

export const useConfiguratorCommerceRuntime = (
  configuration: ConfiguratorConfiguration,
): ConfiguratorCommerceRuntime => {
  const { selection, status } = usePriceReferenceSelection();
  const [quantity, setQuantityState] = useState(1);
  const [settledQuantity, setSettledQuantity] = useState(1);
  const [priceReference, setPriceReferenceState] = useState(() => selection?.initialValue.code ?? "");
  const [submission, setSubmission] = useState<CommerceSubmission | null>(null);

  useEffect(() => {
    if (quantity === settledQuantity) {
      return;
    }
    const timer = window.setTimeout(() => setSettledQuantity(quantity), 500);
    return () => window.clearTimeout(timer);
  }, [quantity, settledQuantity]);

  useEffect(() => {
    setSubmission((current) =>
      current &&
      (current.configId !== configuration.configId ||
        current.quantity !== quantity ||
        current.priceReference !== priceReference)
        ? null
        : current,
    );
  }, [configuration.configId, priceReference, quantity]);

  const setQuantity = useCallback((nextQuantity: number) => {
    setQuantityState(normalizeQuantity(nextQuantity));
    setSubmission(null);
  }, []);
  const setPriceReference = useCallback((nextPriceReference: string) => {
    setPriceReferenceState(nextPriceReference);
    setSubmission(null);
  }, []);
  const markSubmitted = useCallback(
    (configId = configuration.configId) => {
      setSubmission({ configId, quantity, priceReference });
    },
    [configuration.configId, priceReference, quantity],
  );
  const clearSubmission = useCallback(() => setSubmission(null), []);

  const priceReferences = useMemo(
    () =>
      selection?.options.map((option) => {
        const selectOption = toSelectOption(option);
        return {
          value: option.code,
          label: option.code ? selectOption.label : option.description?.trim() || selectOption.label,
        };
      }) ?? [],
    [selection],
  );

  return useMemo(
    () => ({
      quantity,
      settledQuantity,
      priceReference,
      priceReferences,
      priceReferenceEditable: status === "ready" && priceReferences.length > 1,
      isSettling: quantity !== settledQuantity,
      submission,
      setQuantity,
      setPriceReference,
      markSubmitted,
      clearSubmission,
    }),
    [
      clearSubmission,
      markSubmitted,
      priceReference,
      priceReferences,
      quantity,
      setPriceReference,
      setQuantity,
      settledQuantity,
      status,
      submission,
    ],
  );
};
